import { useState, useEffect } from 'react';
import { AdminShell } from '@/components/AdminShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Save, Pencil, Trash2, Package } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const emptyProduct = {
  name: '', tagline: '', description: '', extended_description: '',
  features: '', price: 0, currency: 'USD', prelaunch: true, prelaunch_message: '', status: 'active'
};

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(emptyProduct);
  const [featuresText, setFeaturesText] = useState('');
  const [editId, setEditId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchProducts = () => api.get('/admin/products').then(res => setProducts(res.data)).catch(console.error);
  useEffect(() => { fetchProducts(); }, []);

  const handleEdit = (product) => {
    setEditId(product._id);
    setForm({
      name: product.name || '',
      tagline: product.tagline || '',
      description: product.description || '',
      extended_description: product.extended_description || '',
      features: '',
      price: product.price || 0,
      currency: product.currency || 'USD',
      prelaunch: product.prelaunch ?? true,
      prelaunch_message: product.prelaunch_message || '',
      status: product.status || 'active'
    });
    setFeaturesText(product.features?.join('\n') || '');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      const features = featuresText.split('\n').map(f => f.trim()).filter(Boolean);
      if (editId) {
        await api.put(`/admin/products/${editId}`, {
          name: form.name,
          tagline: form.tagline,
          description: form.description,
          extended_description: form.extended_description,
          features,
          price: parseFloat(form.price) || 0,
          prelaunch: form.prelaunch,
          prelaunch_message: form.prelaunch_message,
          status: form.status
        });
        toast.success('Product updated.');
      } else {
        await api.post('/admin/products', {
          name: form.name,
          tagline: form.tagline,
          description: form.description,
          extended_description: form.extended_description,
          features,
          price: parseFloat(form.price) || 0,
          currency: form.currency,
          prelaunch: form.prelaunch,
          prelaunch_message: form.prelaunch_message,
          status: form.status
        });
        toast.success('Product created.');
      }
      setDialogOpen(false);
      setForm(emptyProduct);
      setFeaturesText('');
      setEditId(null);
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save product.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product? This cannot be undone.')) return;
    try {
      await api.delete(`/admin/products/${id}`);
      toast.success('Product deleted.');
      fetchProducts();
    } catch (err) {
      toast.error('Failed to delete product.');
    }
  };

  return (
    <AdminShell title="Products">
      <div className="flex items-center justify-between mb-6">
        <p className="font-['Inter'] text-sm text-[hsl(var(--muted-foreground))]">
          {products.length} product{products.length !== 1 ? 's' : ''}
        </p>
        <Dialog open={dialogOpen} onOpenChange={(v) => { setDialogOpen(v); if (!v) { setForm(emptyProduct); setFeaturesText(''); setEditId(null); } }}>
          <DialogTrigger asChild>
            <Button
              className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-['Inter'] text-sm"
              data-testid="admin-product-create-button"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Product
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[hsl(166,24%,11%)] border-[rgba(201,168,76,0.2)] max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-['Cormorant_Garamond'] text-2xl font-bold text-[hsl(var(--foreground))]">
                {editId ? 'Edit Product' : 'New Product'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))] mb-2 block">Product Name</label>
                  <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="bg-[rgba(7,10,10,0.5)] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] font-['Inter'] text-sm" />
                </div>
                <div>
                  <label className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))] mb-2 block">Tagline</label>
                  <Input value={form.tagline} onChange={e => setForm({...form, tagline: e.target.value})} className="bg-[rgba(7,10,10,0.5)] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] font-['Inter'] text-sm" />
                </div>
              </div>
              <div>
                <label className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))] mb-2 block">Description</label>
                <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3} className="bg-[rgba(7,10,10,0.5)] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] font-['Inter'] text-sm resize-none" />
              </div>
              <div>
                <label className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))] mb-2 block">Extended Description</label>
                <Textarea value={form.extended_description} onChange={e => setForm({...form, extended_description: e.target.value})} rows={4} className="bg-[rgba(7,10,10,0.5)] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] font-['Inter'] text-sm resize-none" />
              </div>
              <div>
                <label className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))] mb-2 block">Features (one per line)</label>
                <Textarea value={featuresText} onChange={e => setFeaturesText(e.target.value)} rows={4} className="bg-[rgba(7,10,10,0.5)] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] font-['Inter'] text-sm resize-none" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))] mb-2 block">Price (USD)</label>
                  <Input type="number" step="0.01" value={form.price} onChange={e => setForm({...form, price: e.target.value})} className="bg-[rgba(7,10,10,0.5)] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] font-['Inter'] text-sm" />
                </div>
                <div>
                  <label className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))] mb-2 block">Pre-Launch Mode</label>
                  <div className="flex items-center gap-3 mt-2">
                    <Switch
                      checked={form.prelaunch}
                      onCheckedChange={v => setForm({...form, prelaunch: v})}
                    />
                    <span className="font-['Inter'] text-sm text-[hsl(var(--muted-foreground))]">
                      {form.prelaunch ? 'Waitlist mode' : 'Purchase mode'}
                    </span>
                  </div>
                </div>
              </div>
              {form.prelaunch && (
                <div>
                  <label className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))] mb-2 block">Pre-Launch Message</label>
                  <Textarea value={form.prelaunch_message} onChange={e => setForm({...form, prelaunch_message: e.target.value})} rows={2} className="bg-[rgba(7,10,10,0.5)] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] font-['Inter'] text-sm resize-none" />
                </div>
              )}
              <Button onClick={handleSave} className="w-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-['Inter'] text-sm font-medium py-5 rounded-lg">
                <Save className="h-4 w-4 mr-2" /> {editId ? 'Update Product' : 'Create Product'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Product Cards */}
      <div className="space-y-4">
        {products.length === 0 ? (
          <div className="text-center py-20">
            <Package className="h-12 w-12 text-[hsl(var(--muted-foreground))] mx-auto mb-4" />
            <p className="font-['Inter'] text-sm text-[hsl(var(--muted-foreground))]">No products yet.</p>
            <p className="font-['Inter'] text-xs text-[hsl(var(--silver))] mt-2">Create your first product to get started.</p>
          </div>
        ) : products.map(product => (
          <Card key={product._id} className="bg-[hsl(var(--card))] border-[rgba(201,168,76,0.15)]">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <div className="flex-1 min-w-0">
                <CardTitle className="font-['Cormorant_Garamond'] text-2xl font-bold text-[hsl(var(--foreground))]">
                  {product.name}
                </CardTitle>
                {product.tagline && (
                  <p className="font-['Cormorant_Garamond'] text-base italic text-[hsl(var(--champagne))] mt-1">
                    {product.tagline}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-3 shrink-0 ml-4">
                <Badge variant="outline" className={`font-['Inter'] text-[10px] uppercase ${
                  product.prelaunch ? 'border-[rgba(201,168,76,0.3)] text-[hsl(var(--gold))]' : 'border-[rgba(122,158,126,0.4)] text-[hsl(var(--sage))]'
                }`}>
                  {product.prelaunch ? 'Pre-Launch' : 'Live'}
                </Badge>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-[hsl(var(--silver))] hover:text-[hsl(var(--foreground))]" onClick={() => handleEdit(product)}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-[hsl(var(--silver))] hover:text-red-400" onClick={() => handleDelete(product._id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="font-['Inter'] text-[10px] font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))] mb-1">Price</p>
                  <p className="font-['Cormorant_Garamond'] text-lg font-bold text-[hsl(var(--gold))]">
                    ${product.price?.toFixed(2)} {product.currency}
                  </p>
                </div>
                <div>
                  <p className="font-['Inter'] text-[10px] font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))] mb-1">Slug</p>
                  <p className="font-['Inter'] text-sm text-[hsl(var(--muted-foreground))]">{product.slug}</p>
                </div>
                <div>
                  <p className="font-['Inter'] text-[10px] font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))] mb-1">Features</p>
                  <p className="font-['Inter'] text-sm text-[hsl(var(--muted-foreground))]">{product.features?.length || 0} listed</p>
                </div>
              </div>
              {product.description && (
                <p className="font-['Inter'] text-sm text-[hsl(var(--muted-foreground))] mt-4 line-clamp-2">
                  {product.description}
                </p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </AdminShell>
  );
}
